/**
 * Wartungspläne — `MAINT-001`, nach `07` §7.16.
 *
 * Der Abschnitt nennt die Felder — „Vorlage, Intervall, Bezugsdatum, Vorlauf,
 * Zuständigkeit, nächste Fälligkeit und Status" — und **vier Aktionen**: „Das
 * Büro kann einen Plan pausieren, einmalig verschieben, eine Fälligkeit
 * überspringen oder nachholen."
 *
 * Die vier sind nicht austauschbar, und das ist der Grund, aus dem sie
 * einzeln dastehen:
 *
 *   pausieren     Der Plan ruht. Es entsteht nichts mehr, bis jemand ihn
 *                 wieder anschaltet — etwa während eines Umbaus.
 *   verschieben   **Einmalig.** Der Rhythmus bleibt: Wer die Märzwartung auf
 *                 April schiebt, hat im September trotzdem wieder eine.
 *   überspringen  Diese Fälligkeit entfällt ersatzlos, die nächste kommt
 *                 nach Plan.
 *   nachholen     Eine versäumte Fälligkeit wird jetzt fällig.
 *
 * Verschieben und Überspringen sehen sich ähnlich und sind es nicht: Das eine
 * verlegt einen Termin, das andere lässt ihn ausfallen. Eine gemeinsame
 * Aktion „Termin ändern" würde die Frage „hat der Kunde die Wartung bekommen"
 * unbeantwortbar machen.
 *
 * **Was hier nicht passiert: Einsätze erzeugen.** `MAINT-001` liegt in Phase 7,
 * und `07` §7.16 hält fest, dass automatische Erzeugung „keine abgeschlossenen
 * Historieneinträge verändern" darf. Diese Ansicht plant; das Anlegen bleibt
 * bis dahin Handarbeit, und der Hinweis dazu steht unten.
 */

import { useState } from "react";
import { Link } from "react-router";

import { findeEinsatz, findeKunde, findePerson } from "@hwc/demo-data";
import type { Wartungsplan } from "@hwc/demo-data";
import { darfEingangBearbeiten } from "@hwc/auth";
import { Knopf, Zustand } from "@hwc/ui";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { planeWartung } from "../../src/daten/vorgaenge.js";
import {
  Feld,
  Felder,
  Leer,
  Liste,
  Zeile,
  datum,
  zumKunden,
} from "../../src/bereiche/bausteine.js";

export function Wartung() {
  const { bestand, heute, rollen } = useSitzung();
  const [offen, setzeOffen] = useState<string | undefined>(undefined);

  const plaene = bestand.wartungsplaene.slice().sort((a, b) => {
    // Pausierte nach unten: Sie fordern nichts. Sonst nach Fälligkeit — was
    // als Nächstes ansteht, steht oben.
    if (a.status !== b.status) return a.status === "aktiv" ? -1 : 1;
    return a.naechsteFaelligkeit.localeCompare(b.naechsteFaelligkeit);
  });

  const heuteTag = heute.toISOString().slice(0, 10);

  return (
    <>
      <div className="bereichskopf">
        <h1>Wartung</h1>
        <span className="bereichskopf__zusatz">{plaene.length} Pläne</span>
      </div>

      {plaene.length === 0 ? (
        <Leer text="Es ist kein Wartungsplan angelegt." />
      ) : (
        <Liste>
          {plaene.map((p) => {
            const kunde = findeKunde(bestand, p.kunde);
            const ueberfaellig = p.status === "aktiv" && p.naechsteFaelligkeit < heuteTag;
            const imVorlauf =
              p.status === "aktiv"
              && !ueberfaellig
              && Date.parse(`${p.naechsteFaelligkeit}T00:00:00Z`) - heute.getTime()
                <= p.vorlaufTage * 86_400_000;

            return (
              <Zeile
                key={p.ref.opaqueId}
                titel={
                  <button
                    type="button"
                    className="zeile__oeffnen"
                    onClick={() =>
                      setzeOffen(offen === p.ref.opaqueId ? undefined : p.ref.opaqueId)
                    }
                    aria-expanded={offen === p.ref.opaqueId}
                  >
                    {p.titel}
                  </button>
                }
                unterzeile={
                  <>
                    {kunde?.name} · alle {p.intervallMonate} Monate ·{" "}
                    {findePerson(bestand, p.zustaendig)?.name}
                  </>
                }
                abzeichen={
                  <>
                    {ueberfaellig && (
                      <span className="abzeichen abzeichen--fehler">überfällig</span>
                    )}
                    {imVorlauf && (
                      <span className="abzeichen abzeichen--warnung">im Vorlauf</span>
                    )}
                    {p.status !== "aktiv" && (
                      <span className="abzeichen abzeichen--neutral">{p.status}</span>
                    )}
                  </>
                }
                rechts={`fällig ${datum(p.naechsteFaelligkeit)}`}
              />
            );
          })}
        </Liste>
      )}

      {/*
        `key` aus demselben Grund wie im Eingangskorb: Ohne ihn bleibt die
        Absage des vorigen Plans stehen, wenn man zum nächsten wechselt — eine
        Fehlermeldung über etwas, das man gerade gar nicht ansieht.
      */}
      {offen && (
        <Plandetail
          key={offen}
          plan={plaene.find((p) => p.ref.opaqueId === offen)!}
        />
      )}

      <p className="notiz">
        {/*
          Die Grenze dieser Ansicht, und sie gehört sichtbar hierher: Wer
          plant, nimmt sonst an, dass daraus auch etwas entsteht.
        */}
        <strong>Aus einem Plan entsteht noch kein Einsatz.</strong> Die Erzeugung baut{" "}
        <code>MAINT-001</code> in Phase 7 — mit Idempotenzschlüsseln, damit ein zweiter
        Lauf keinen zweiten Einsatz anlegt, und ohne abgeschlossene Historieneinträge zu
        verändern (<code>07</code> §7.16). Bis dahin legt das Büro den Einsatz von Hand
        an, wenn eine Fälligkeit in den Vorlauf rutscht.
      </p>

      {!darfEingangBearbeiten(rollen) && (
        <p className="notiz">
          Ihre Rolle kann Pläne lesen, aber nicht ändern.
        </p>
      )}
    </>
  );
}

function Plandetail({ plan }: { plan: Wartungsplan }) {
  const { bestand, person, rollen, fuehreAus } = useSitzung();
  const [absage, setzeAbsage] = useState<string | null>(null);

  const kunde = findeKunde(bestand, plan.kunde);
  const letzter = findeEinsatz(bestand, plan.letzterEinsatz);
  const darf = darfEingangBearbeiten(rollen);

  const tun = (
    aktion: Parameters<typeof planeWartung>[0]["aktion"],
    tage?: number,
  ) => {
    const grund = fuehreAus((v) =>
      planeWartung({
        vorgaenge: v,
        plan,
        aktion,
        akteur: person.ref,
        ...(tage !== undefined ? { tage } : {}),
      }),
    );
    setzeAbsage(grund ?? null);
  };

  return (
    <section className="karte">
      <h2 style={{ marginTop: 0 }}>{plan.titel}</h2>

      {absage && <Zustand art="warnung" text={absage} />}

      <Felder>
        <Feld name="Kunde">
          {kunde ? <Link to={zumKunden(kunde.nummer)}>{kunde.name}</Link> : "—"}
        </Feld>
        <Feld name="Intervall">alle {plan.intervallMonate} Monate</Feld>
        <Feld name="Bezugsdatum">{datum(plan.bezugsdatum)}</Feld>
        <Feld name="Vorlauf">{plan.vorlaufTage} Tage</Feld>
        <Feld name="Zuständig">{findePerson(bestand, plan.zustaendig)?.name ?? "—"}</Feld>
        <Feld name="Nächste Fälligkeit">{datum(plan.naechsteFaelligkeit)}</Feld>
        <Feld name="Status">{plan.status}</Feld>
        <Feld name="Vorherige Wartung">
          {/*
            `07` §7.16: „Jeder erzeugte Einsatz zeigt seinen Wartungsplan und
            die vorherige Wartung." Die Rückrichtung gehört genauso dazu — wer
            entscheidet, ob eine Fälligkeit übersprungen werden kann, will
            wissen, wann zuletzt jemand da war.
          */}
          {letzter ? `${letzter.titel} (${datum(letzter.von)})` : "keine erfasst"}
        </Feld>
      </Felder>

      {darf && (
        <>
          <h3>Die vier Aktionen aus `07` §7.16</h3>
          <div className="filter">
            <Knopf
              type="button"
              art={plan.status === "aktiv" ? "neben" : "haupt"}
              onClick={() => tun(plan.status === "aktiv" ? "pausieren" : "fortsetzen")}
            >
              {plan.status === "aktiv" ? "Pausieren" : "Fortsetzen"}
            </Knopf>
            <Knopf
              type="button"
              art="neben"
              disabled={plan.status !== "aktiv"}
              onClick={() => tun("verschieben", 14)}
            >
              Um 14 Tage verschieben
            </Knopf>
            <Knopf
              type="button"
              art="neben"
              disabled={plan.status !== "aktiv"}
              onClick={() => tun("ueberspringen")}
            >
              Fälligkeit überspringen
            </Knopf>
            <Knopf
              type="button"
              art="neben"
              disabled={plan.status !== "aktiv"}
              onClick={() => tun("nachholen")}
            >
              Jetzt nachholen
            </Knopf>
          </div>

          <p className="notiz">
            <strong>Verschieben und Überspringen sind nicht dasselbe.</strong>{" "}
            Verschieben verlegt diesen einen Termin und lässt den Rhythmus stehen;
            Überspringen lässt die Fälligkeit ausfallen und geht zur übernächsten. Wer
            beides zusammenwirft, kann später nicht mehr sagen, ob der Kunde die Wartung
            bekommen hat.
          </p>
        </>
      )}
    </section>
  );
}
